'use client';

/**
 * Adapted from react-bits' StickerPeel (npx shadcn@latest add @react-bits/StickerPeel-TS-TW,
 * https://reactbits.dev). The upstream component renders a flat `imageSrc` in
 * three layers (main sticker, blurred drop-shadow copy, peeled-back lit copy).
 * ServiceStickerBadge's artwork is a procedural inline SVG (`ServiceSticker`,
 * different per service kind/color variant) rather than a static image file,
 * so this fork swaps each `<img src={imageSrc}>` layer for a `<div>` wrapping
 * the same `children` node — CSS transform/filter apply identically to a div
 * wrapping an SVG as they do to an <img>, so the peel/shadow/lighting effects
 * are unchanged; only the media source changed from file-based to component-based.
 */

import { useRef, useEffect, useMemo, type CSSProperties, type ReactNode } from 'react';

interface StickerPeelProps {
    children: ReactNode;
    rotate?: number;
    peelBackHoverPct?: number;
    peelBackActivePct?: number;
    peelEasing?: string;
    peelHoverEasing?: string;
    /** Tailwind width classes for the sticker content (e.g. `w-32 md:w-44`).
     *  Upstream react-bits' StickerPeel takes a numeric `width` px prop since
     *  it sizes a flat `imageSrc`; this fork sizes a responsive component tree
     *  instead, so a className fits the codebase's existing per-call-site
     *  responsive sizing (`sizeClassName`) better than a fixed px number. */
    contentClassName?: string;
    shadowIntensity?: number;
    lightingIntensity?: number;
    initialPosition?: 'center' | { x: number; y: number };
    peelDirection?: number;
    className?: string;
}

interface CSSVars extends CSSProperties {
    '--sticker-rotate'?: string;
    '--sticker-p'?: string;
    '--sticker-peelback-hover'?: string;
    '--sticker-peelback-active'?: string;
    '--sticker-peel-easing'?: string;
    '--sticker-peel-hover-easing'?: string;
    '--sticker-width'?: string;
    '--sticker-shadow-opacity'?: number;
    '--sticker-lighting-constant'?: number;
    '--peel-direction'?: string;
    '--sticker-start'?: string;
    '--sticker-end'?: string;
}

const StickerPeel: React.FC<StickerPeelProps> = ({
    children,
    rotate = 30,
    peelBackHoverPct = 30,
    peelBackActivePct = 40,
    peelEasing = 'power3.out',
    peelHoverEasing = 'power2.out',
    contentClassName = 'w-40',
    shadowIntensity = 0.6,
    lightingIntensity = 0.1,
    initialPosition = 'center',
    peelDirection = 0,
    className = '',
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const dragTargetRef = useRef<HTMLDivElement>(null);
    const pointLightRef = useRef<SVGFEPointLightElement>(null);
    const pointLightFlippedRef = useRef<SVGFEPointLightElement>(null);
    const positionRef = useRef({ x: 0, y: 0 });

    const defaultPadding = 12;

    useEffect(() => {
        const target = dragTargetRef.current;
        if (!target) return;

        const boundsEl = target.parentNode as HTMLElement;
        let activePointerId: number | null = null;
        let startClientX = 0;
        let startClientY = 0;
        let startX = 0;
        let startY = 0;
        let previousClientX = 0;

        const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

        const constrain = (x: number, y: number) => ({
            x: clamp(x, 0, Math.max(0, boundsEl.clientWidth - target.offsetWidth)),
            y: clamp(y, 0, Math.max(0, boundsEl.clientHeight - target.offsetHeight)),
        });

        const renderPosition = (x: number, y: number, rotation = 0, animate = false) => {
            positionRef.current = { x, y };
            target.style.transition = animate
                ? 'transform 600ms cubic-bezier(0.16, 1, 0.3, 1)'
                : 'none';
            target.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${rotation}deg)`;
        };

        if (typeof initialPosition === 'object') {
            const initial = constrain(initialPosition.x, initialPosition.y);
            renderPosition(initial.x, initial.y);
        }

        const handlePointerDown = (event: PointerEvent) => {
            if (event.button !== 0 || activePointerId !== null) return;

            activePointerId = event.pointerId;
            startClientX = event.clientX;
            startClientY = event.clientY;
            previousClientX = event.clientX;
            startX = positionRef.current.x;
            startY = positionRef.current.y;
            target.setPointerCapture(event.pointerId);
            target.style.transition = 'none';
            event.preventDefault();
        };

        const handlePointerMove = (event: PointerEvent) => {
            if (event.pointerId !== activePointerId) return;

            const next = constrain(
                startX + event.clientX - startClientX,
                startY + event.clientY - startClientY,
            );
            const rotation = clamp((event.clientX - previousClientX) * 0.4, -24, 24);
            previousClientX = event.clientX;
            renderPosition(next.x, next.y, rotation);
        };

        const handlePointerEnd = (event: PointerEvent) => {
            if (event.pointerId !== activePointerId) return;
            activePointerId = null;
            if (target.hasPointerCapture(event.pointerId)) {
                target.releasePointerCapture(event.pointerId);
            }
            renderPosition(positionRef.current.x, positionRef.current.y, 0, true);
        };

        const handleResize = () => {
            const next = constrain(positionRef.current.x, positionRef.current.y);
            if (next.x !== positionRef.current.x || next.y !== positionRef.current.y) {
                renderPosition(next.x, next.y, 0, true);
            }
        };

        target.addEventListener('pointerdown', handlePointerDown);
        target.addEventListener('pointermove', handlePointerMove);
        target.addEventListener('pointerup', handlePointerEnd);
        target.addEventListener('pointercancel', handlePointerEnd);
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            target.removeEventListener('pointerdown', handlePointerDown);
            target.removeEventListener('pointermove', handlePointerMove);
            target.removeEventListener('pointerup', handlePointerEnd);
            target.removeEventListener('pointercancel', handlePointerEnd);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [initialPosition]);

    useEffect(() => {
        const updateLight = (e: Event) => {
            const mouseEvent = e as MouseEvent;
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return;

            const x = mouseEvent.clientX - rect.left;
            const y = mouseEvent.clientY - rect.top;

            if (pointLightRef.current) {
                pointLightRef.current.setAttribute('x', String(x));
                pointLightRef.current.setAttribute('y', String(y));
            }

            const normalizedAngle = Math.abs(peelDirection % 360);
            if (pointLightFlippedRef.current) {
                if (normalizedAngle !== 180) {
                    pointLightFlippedRef.current.setAttribute('x', String(x));
                    pointLightFlippedRef.current.setAttribute('y', String(rect.height - y));
                } else {
                    pointLightFlippedRef.current.setAttribute('x', '-1000');
                    pointLightFlippedRef.current.setAttribute('y', '-1000');
                }
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('mousemove', updateLight);
            return () => container.removeEventListener('mousemove', updateLight);
        }
    }, [peelDirection]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleTouchStart = () => container.classList.add('touch-active');
        const handleTouchEnd = () => container.classList.remove('touch-active');

        container.addEventListener('touchstart', handleTouchStart);
        container.addEventListener('touchend', handleTouchEnd);
        container.addEventListener('touchcancel', handleTouchEnd);

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchend', handleTouchEnd);
            container.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, []);

    const cssVars: CSSVars = useMemo(
        () => ({
            '--sticker-rotate': `${rotate}deg`,
            '--sticker-p': `${defaultPadding}px`,
            '--sticker-peelback-hover': `${peelBackHoverPct}%`,
            '--sticker-peelback-active': `${peelBackActivePct}%`,
            '--sticker-peel-easing': peelEasing,
            '--sticker-peel-hover-easing': peelHoverEasing,
            '--sticker-shadow-opacity': shadowIntensity,
            '--sticker-lighting-constant': lightingIntensity,
            '--peel-direction': `${peelDirection}deg`,
            '--sticker-start': `calc(-1 * ${defaultPadding}px)`,
            '--sticker-end': `calc(100% + ${defaultPadding}px)`,
        }),
        [rotate, peelBackHoverPct, peelBackActivePct, peelEasing, peelHoverEasing, shadowIntensity, lightingIntensity, peelDirection]
    );

    const stickerMainStyle: CSSProperties = {
        clipPath: `polygon(var(--sticker-start) var(--sticker-start), var(--sticker-end) var(--sticker-start), var(--sticker-end) var(--sticker-end), var(--sticker-start) var(--sticker-end))`,
        transition: 'clip-path 0.6s ease-out',
        filter: 'url(#dropShadow)',
        willChange: 'clip-path, transform',
    };

    const flapStyle: CSSProperties = {
        clipPath: `polygon(var(--sticker-start) var(--sticker-start), var(--sticker-end) var(--sticker-start), var(--sticker-end) var(--sticker-start), var(--sticker-start) var(--sticker-start))`,
        top: `calc(-100% - var(--sticker-p) - var(--sticker-p))`,
        transform: 'scaleY(-1)',
        transition: 'all 0.6s ease-out',
        willChange: 'clip-path, transform',
    };

    const contentStyle: CSSProperties = {
        transform: `rotate(calc(${rotate}deg - ${peelDirection}deg))`,
    };

    const shadowContentStyle: CSSProperties = { ...contentStyle, filter: 'url(#expandAndFill)' };

    return (
        <div className={`absolute cursor-grab touch-none active:cursor-grabbing transform-gpu ${className}`} ref={dragTargetRef} style={cssVars}>
            <style
                dangerouslySetInnerHTML={{
                    __html: `
          .sticker-container:hover .sticker-main,
          .sticker-container.touch-active .sticker-main {
            clip-path: polygon(var(--sticker-start) var(--sticker-peelback-hover), var(--sticker-end) var(--sticker-peelback-hover), var(--sticker-end) var(--sticker-end), var(--sticker-start) var(--sticker-end)) !important;
          }
          .sticker-container:hover .sticker-flap,
          .sticker-container.touch-active .sticker-flap {
            clip-path: polygon(var(--sticker-start) var(--sticker-start), var(--sticker-end) var(--sticker-start), var(--sticker-end) var(--sticker-peelback-hover), var(--sticker-start) var(--sticker-peelback-hover)) !important;
            top: calc(-100% + 2 * var(--sticker-peelback-hover) - 1px) !important;
          }
          .sticker-container:active .sticker-main {
            clip-path: polygon(var(--sticker-start) var(--sticker-peelback-active), var(--sticker-end) var(--sticker-peelback-active), var(--sticker-end) var(--sticker-end), var(--sticker-start) var(--sticker-end)) !important;
          }
          .sticker-container:active .sticker-flap {
            clip-path: polygon(var(--sticker-start) var(--sticker-start), var(--sticker-end) var(--sticker-start), var(--sticker-end) var(--sticker-peelback-active), var(--sticker-start) var(--sticker-peelback-active)) !important;
            top: calc(-100% + 2 * var(--sticker-peelback-active) - 1px) !important;
          }
        `,
                }}
            />

            <svg width="0" height="0">
                <defs>
                    <filter id="pointLight">
                        <feGaussianBlur stdDeviation="1" result="blur" />
                        <feSpecularLighting result="spec" in="blur" specularExponent={100} specularConstant={lightingIntensity} lightingColor="white">
                            <fePointLight ref={pointLightRef} x={100} y={100} z={300} />
                        </feSpecularLighting>
                        <feComposite in="spec" in2="SourceGraphic" result="lit" />
                        <feComposite in="lit" in2="SourceAlpha" operator="in" />
                    </filter>

                    <filter id="pointLightFlipped">
                        <feGaussianBlur stdDeviation="10" result="blur" />
                        <feSpecularLighting result="spec" in="blur" specularExponent={100} specularConstant={lightingIntensity * 7} lightingColor="white">
                            <fePointLight ref={pointLightFlippedRef} x={100} y={100} z={300} />
                        </feSpecularLighting>
                        <feComposite in="spec" in2="SourceGraphic" result="lit" />
                        <feComposite in="lit" in2="SourceAlpha" operator="in" />
                    </filter>

                    <filter id="dropShadow">
                        <feDropShadow dx="2" dy="4" stdDeviation={3 * shadowIntensity} floodColor="black" floodOpacity={shadowIntensity} />
                    </filter>

                    <filter id="expandAndFill">
                        <feOffset dx="0" dy="0" in="SourceAlpha" result="shape" />
                        <feFlood floodColor="rgb(179,179,179)" result="flood" />
                        <feComposite operator="in" in="flood" in2="shape" />
                    </filter>
                </defs>
            </svg>

            <div
                className="sticker-container relative select-none touch-none sm:touch-auto"
                ref={containerRef}
                style={{
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    transform: `rotate(${peelDirection}deg)`,
                    transformOrigin: 'center',
                }}
            >
                <div className="sticker-main" style={stickerMainStyle}>
                    <div style={{ filter: 'url(#pointLight)' }}>
                        <div className={contentClassName} style={contentStyle}>{children}</div>
                    </div>
                </div>

                <div className="absolute top-4 left-2 w-full h-full opacity-40" style={{ filter: 'brightness(0) blur(8px)' }}>
                    <div className="sticker-flap" style={flapStyle}>
                        <div className={contentClassName} style={shadowContentStyle}>{children}</div>
                    </div>
                </div>

                <div className="sticker-flap absolute w-full h-full left-0" style={flapStyle}>
                    <div style={{ filter: 'url(#pointLightFlipped)' }}>
                        <div className={contentClassName} style={shadowContentStyle}>{children}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StickerPeel;
